import { Button, CodeEntry, Grid, Text, useToast, Link, usePhoneNumber } from '@afterpaytouch/core'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { AppLink, FormWrapper, TransMaskedText, ProfileCard } from '..'
import { Trans } from '../../utils/convergence'
import { SEND_TIMEOUT_MS } from '../../config'
import { Route } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import {
  accountLookup,
  loginWithOneTimePassword,
  sendOneTimePassword,
  useAppDispatch,
  useAutoSendOTP,
  captureCashConsent,
  useIsCashApp,
  useLogLoginAttemptEvent,
  useAppRouter,
  useIsOtpAsPrimaryLoginMethodEnabled,
  useConsumerSelector,
  useCountry,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { SupportedLocale } from '../../model'
import { CashSignInTerms } from '../CashSignInTerms'
import clsx from 'clsx'
import styles from './style.module.css'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { useTranslation } from '../../utils/convergence'
import { useProfileCardProps } from './hooks'

interface Props {
  onSuccess: (locale: SupportedLocale) => void
  onAccountLocked: () => void
  onSecondaryRoute?: () => void
}

const errorKeys: Partial<Record<ErrorCode, string>> = {
  [ErrorCode.AuthenticationFailure]: 'auth',
  [ErrorCode.AuthenticationFailureMaxAttempts]: 'authMaxAttempt',
  [ErrorCode.TooManyOTPRequests]: 'tooManyOTPRequests',
  [ErrorCode.PaywatchCheckFailed]: 'paywatchCheckFailed',
}

export const OneTimePassword: FunctionComponent<Props> = ({ onSuccess, onAccountLocked, onSecondaryRoute = () => undefined }) => {
  const { t } = useTranslation()
  const countryCode = useCountry()
  const { mobile: rawMobile } = useConsumerSelector()
  const mobile = usePhoneNumber({ country: countryCode }).formatIntMaskedNumber(rawMobile)
  const { textSize, buttonSize } = useCheckoutSizes()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const setToast = useToast()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const autoSend = useAutoSendOTP()
  const profileCardProps = useProfileCardProps()
  const [codeEntryReset, setCodeEntryReset] = useState(0)
  const [disableCodeEntry, setDisableCodeEntry] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [disableSend, setDisableSend] = useState(false)
  const [disableFromTooManyRequests, setDisableFromTooManyRequests] = useState(false)
  const codeEntryRef = useRef<HTMLInputElement | undefined>()

  const disabled = disableSend || disableFromTooManyRequests
  const isOtpAsPrimaryLoginEnabled = useIsOtpAsPrimaryLoginMethodEnabled()

  const isCashApp = useIsCashApp()
  const logLoginAttemptEvent = useLogLoginAttemptEvent({ is2fa: false })

  const onCodeComplete = async (code: string): Promise<void> => {
    try {
      setDisableCodeEntry(true)
      setDisableSend(true)
      setErrorMessage('')

      unwrapResult(await dispatch(loginWithOneTimePassword(code)))
      const account = unwrapResult(await dispatch(accountLookup()))

      if (isCashApp) {
        dispatch(captureCashConsent())
      }
      onSuccess(account.preferredLocale)

      logLoginAttemptEvent('OTP', true)
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      if (errorCode === ErrorCode.AccountLocked) {
        onAccountLocked()
        return
      }
      if (errorCode === ErrorCode.ForcePasswordResetRequired) {
        router.push(Route.PASSWORD_RESET_REQUIRED)
        return
      }

      if (errorCode === ErrorCode.AuthenticationFailureMaxAttempts) {
        setDisableCodeEntry(true)
      } else {
        setDisableCodeEntry(false)
      }

      if (errorCode === ErrorCode.PaywatchCheckFailed) {
        setDisableCodeEntry(true)
      }

      setCodeEntryReset(codeEntryReset + 1)
      setErrorMessage(`oneTimePassword:error.${errorKeys[errorCode] ?? 'unknown'}`)
      setDisableSend(false)

      logLoginAttemptEvent('OTP', false)
    }
  }

  const failedToSendCodeMessage = (): JSX.Element => {
    return (
      <Text>
        <Trans
          i18nKey={'oneTimePassword:error.codeSentFail'}
          components={{
            mobile: <TransMaskedText actionName='OTP Mobile Number' text={mobile} testNameSpace='otp-toast-code-sent-fail-mobile' />,
          }}
        />
      </Text>
    )
  }

  const sendOTP = async (): Promise<void> => {
    try {
      setDisableSend(true)
      setErrorMessage('')
      unwrapResult(await dispatch(sendOneTimePassword()))
      setCodeEntryReset(codeEntryReset + 1)
      setToast({ message: t('oneTimePassword:codeSent'), kind: 'Success', onShow: () => logEvent(TrackingEvent.VIEWED_ONE_TIME_PASSWORD_SENT_TOAST) })
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error

      if (errorCode === ErrorCode.TooManyOTPRequests) {
        setDisableFromTooManyRequests(true)
      }

      setErrorMessage(`oneTimePassword:error.${errorKeys[errorCode] ?? 'unknown'}`)
      setToast({
        message: failedToSendCodeMessage,
        kind: 'Failure',
        onShow: () => logEvent(TrackingEvent.VIEWED_VERIFY_FAIL_TOAST),
      })
    }
  }

  useEffect(() => {
    if (autoSend) {
      sendOTP().catch(console.error)
    }
  }, [autoSend])

  useEffect(() => {
    if (disableSend) {
      const t = setTimeout(() => setDisableSend(false), SEND_TIMEOUT_MS)
      return () => clearTimeout(t)
    }
  }, [disableSend])

  const renderError = (errorMessage: string): ReactNode => {
    return (
      <Trans
        i18nKey={errorMessage}
        components={{
          password: <AppLink underline noMargin inline route={Route.ENTER_PASSWORD} />,
          helpPaywatch: <Link key='description-link' target='_blank' kind='Underline' href={t('common:paywatchHelpUrl')} />,
          paywatchDesc: <span className={styles.paywatchDescription} />,
        }}
      />
    )
  }

  const renderCTA = (): ReactNode => {
    return isOtpAsPrimaryLoginEnabled ? (
      <div className={clsx(styles.textCenter)}>
        <Text size={textSize}>
          <Trans
            i18nKey={'oneTimePassword:otpExperimentCta'}
            components={{
              resend: <Button.Link size='Regular' testNameSpace='login-otp' disabled={disabled} onClick={sendOTP} />,
              loginWithPass: <Button.Link size='Regular' onClick={onSecondaryRoute} />,
            }}
          />
        </Text>
      </div>
    ) : (
      <Grid.Item>
        <Button.Ghost onClick={sendOTP} size={buttonSize} testNameSpace='login-otp' disabled={disabled} padding='Fluid'>
          {t('oneTimePassword:resend')}
        </Button.Ghost>
      </Grid.Item>
    )
  }

  useEffect(() => {
    const input = codeEntryRef.current
    let focusTimeout
    let clearErrorMessageTimeout

    const handleInput = (): void => {
      clearErrorMessageTimeout = setTimeout(() => setErrorMessage(''), 0)
    }

    if (input != null) {
      focusTimeout = setTimeout(() => {
        input.focus()
      }, 250)

      if (errorMessage !== '') {
        input.addEventListener('input', handleInput)
      }
    }

    return () => {
      clearTimeout(focusTimeout)
      clearTimeout(clearErrorMessageTimeout)
      input?.removeEventListener('input', handleInput)
    }
  }, [codeEntryRef.current])

  return (
    <>
      <FormWrapper>
        <ProfileCard {...profileCardProps} />
        <Grid>
          <MaskedElement actionName='One Time Password Sent Description'>
            <Text size={textSize} id='one-time-password'>
              {t('oneTimePassword:description', { mobile })}
            </Text>
          </MaskedElement>
        </Grid>
        <Grid>
          <MaskedElement actionName='One Time Password Code Input'>
            <CodeEntry
              name='one-time-password'
              aria-labelledby='one-time-password'
              codeEntryLength={6}
              disabled={disableCodeEntry}
              error={errorMessage !== ''}
              errorText={renderError(errorMessage)}
              key={codeEntryReset}
              onComplete={onCodeComplete}
              testNameSpace='login-otp'
              ref={codeEntryRef}
            />
          </MaskedElement>
        </Grid>
        <Grid>{renderCTA()}</Grid>
      </FormWrapper>
      {isCashApp && <CashSignInTerms />}
    </>
  )
}
